"use client";

/**
 * 通用地址搜索组件
 * 
 * 组件提供地址搜索功能，支持模糊查询地址列表
 * 用户可以从下拉列表中选择一个地址，组件会将选中地址的placeId回传给父组件
 */

// React
import React, { useState, useEffect, useMemo, useCallback } from "react";
// 三方库/框架
import { useTranslation } from "@/i18n/client";
import { createFilterOptions } from '@mui/material/Autocomplete';
import { TextField, InputAdornment, Autocomplete } from "@mui/material";
import debounce from 'lodash/debounce';
// 接口
import { GoogleAddress } from "@/apis/address/address.interface";
import { autocomplete } from "@/apis/address/address.api";
// 资源
import AddressClearIcon from "@/assets/icons/address/AddressClearIcon.svg";
import AddressSearchIcon from "@/assets/icons/address/AddressSearchIcon.svg";
// 样式
import styles from "./index.module.scss";

interface AddressSearchProps {
  onSearch: (placeId: string) => void; // 选择地址后的回调函数 传递选中地址的placeId
}

const AddressSearch: React.FC<AddressSearchProps> = ({ 
  onSearch,
}) => {
  const { t } = useTranslation();
  
  const [addressInput, setAddressInput] = useState<string>(""); // 输入框地址
  const [searchResults, setSearchResults] = useState<GoogleAddress[]>([] as GoogleAddress[]); // 模糊查询地址列表
  const [searchLoading, setSearchLoading] = useState<boolean>(false); // 搜索加载状态
  const [searchSelectOpen, setSearchSelectOpen] = useState<boolean>(false); // 搜索下拉框是否开启

  // 地址搜索
  const fetchSearchResults = (val: string) => {
    if(!val) return;
    
    setSearchLoading(true);

    const searchText = val.trim();
    autocomplete({
      searchText
    })
      .then((res) => {
        const list = res?.data || [];
        setSearchResults(list);
      })
      .catch(() => {
        setSearchResults([]);
      })
      .finally(() => {
        setSearchLoading(false);
      })
  };
  
  // 地址搜索防抖
  const debouncedFetchSearchResults = useCallback(
    debounce((searchText: string) => {
      fetchSearchResults(searchText);
    }, 300),
    []
  );

  // 处理地址输入变更
  const handleAddressInputChange = (event: React.SyntheticEvent, value: string | null) => {
    const newValue = value || "";
    setAddressInput(newValue);
    
    if (newValue) {
      if(searchResults?.some(item => item.formattedAddress === newValue)){
        // 输入内容如果在searchResults中存在 不用触发search
        return;
      }

      setSearchSelectOpen(true);
      debouncedFetchSearchResults(newValue);
    } else {
      // 当输入为空时 取消防抖函数 清空搜索结果 关闭下拉框
      debouncedFetchSearchResults.cancel();
      setSearchResults([]);
      setSearchSelectOpen(false);
    }
  };
  
  // 处理选择搜索结果
  const handleSelectSearchResult = (_: React.SyntheticEvent, value: GoogleAddress | string | null) => {
    if (!value || typeof value === 'string') {
      // 不包含地址id 无法匹配地址经纬度信息 直接return
      setAddressInput("");
      return;
    }
    onSearch?.(value.placeId);
  };

  // 自定义Autocomplete选项过滤配置
  const filterOptions = createFilterOptions({
    trim: true,
    stringify: (option: GoogleAddress | string) => typeof option === 'string' ? option : option?.formattedAddress,
  });

  return (
    <>
      <div className={styles.addressSearchContainer}>
        <div className={styles.addressInput}>
          <Autocomplete
            fullWidth
            freeSolo
            disableClearable
            popupIcon={null}
            open={searchSelectOpen}
            inputValue={addressInput}
            options={searchResults}
            loading={searchLoading}
            getOptionLabel={(option) => typeof option === 'string' ? option : option?.formattedAddress}
            filterOptions={filterOptions}
            onClose={() => setSearchSelectOpen(false)}
            onChange={handleSelectSearchResult}
            onInputChange={handleAddressInputChange}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                placeholder={t("address.addressSearchPlaceholder")}
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <InputAdornment position="end">
                      {/* 清除按钮，仅在有输入内容时显示 */}
                      {addressInput && (
                        <AddressClearIcon
                          width={25}
                          onClick={() => {
                            setAddressInput("");
                            setSearchResults([]);
                            setSearchSelectOpen(false);
                            debouncedFetchSearchResults.cancel();
                          }}
                        />
                      )}
                      {/* 搜索图标 */}
                      <AddressSearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </div>
      </div>
    </>
  );
};

export default AddressSearch;
